import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, Zap, Save, Loader2, Plus, Trash2, Download, Eye, EyeOff,
  CheckCircle, X, FileText, Image as ImageIcon, Package, ThumbsUp, ThumbsDown,
} from "lucide-react";
import BrandLogo from "../components/BrandLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const axiosAuth = () => axios.create({
  withCredentials: true,
  headers: { Authorization: `Bearer ${localStorage.getItem("lf_session") || ""}` },
});

const CATEGORIES = [
  { value: "brand_asset", label: "Brand asset" },
  { value: "copy_template", label: "Copy template" },
  { value: "screenshot", label: "Screenshot" },
  { value: "other", label: "Other" },
];

function EditableList({ label, icon: Icon, items, setItems, color = "emerald", testIdPrefix }) {
  const add = () => setItems([...(items || []), ""]);
  const update = (i, v) => { const next = [...items]; next[i] = v; setItems(next); };
  const remove = (i) => setItems((items || []).filter((_, idx) => idx !== i));
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} className={`text-${color}-700`} />
        <span className="font-semibold text-zinc-900 text-sm">{label}</span>
      </div>
      <div className="space-y-2" data-testid={`${testIdPrefix}-list`}>
        {(items || []).map((it, i) => (
          <div key={i} className="flex items-center gap-2">
            <input data-testid={`${testIdPrefix}-item-${i}`} value={it} onChange={e => update(i, e.target.value)}
              className={`flex-1 px-3 py-2 bg-${color}-50/40 border border-${color}-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-${color}-400`} />
            <button onClick={() => remove(i)} data-testid={`${testIdPrefix}-remove-${i}`} className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg"><Trash2 size={13} /></button>
          </div>
        ))}
      </div>
      <button onClick={add} data-testid={`${testIdPrefix}-add`} className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-zinc-700 hover:text-zinc-900"><Plus size={11} /> Add</button>
    </div>
  );
}

function MaterialForm({ onCreated }) {
  const [form, setForm] = useState({ title: "", category: "brand_asset", description: "", body: "", visible: true });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const fileRef = useRef(null);

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (!form.title.trim()) { setErr("Title required"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("category", form.category);
      if (form.description) fd.append("description", form.description);
      if (form.body) fd.append("body", form.body);
      fd.append("visible_to_promoters", String(form.visible));
      if (file) fd.append("file", file);
      await axiosAuth().post(`${API}/admin/promoter-materials`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm({ title: "", category: "brand_asset", description: "", body: "", visible: true });
      setFile(null); if (fileRef.current) fileRef.current.value = "";
      onCreated?.();
    } catch (e) { setErr(e.response?.data?.detail || "Create failed"); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="bg-zinc-50 rounded-2xl p-4 space-y-2" data-testid="material-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <input required data-testid="input-material-title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
          placeholder="Title" className="px-3 py-2 bg-white border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900" />
        <select data-testid="input-material-category" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
          className="px-3 py-2 bg-white border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900">
          {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
      </div>
      <input data-testid="input-material-desc" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
        placeholder="Short description (optional)"
        className="w-full px-3 py-2 bg-white border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900" />
      <textarea rows={3} data-testid="input-material-body" value={form.body} onChange={e => setForm({ ...form, body: e.target.value })}
        placeholder="Copy / instructions (markdown allowed)"
        className="w-full px-3 py-2 bg-white border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900 resize-none" />
      <div className="flex items-center gap-3 flex-wrap">
        <input ref={fileRef} type="file" data-testid="input-material-file" accept=".png,.jpg,.jpeg,.webp,.pdf,.zip"
          onChange={e => setFile(e.target.files?.[0] || null)}
          className="text-xs text-zinc-700 file:mr-3 file:py-1.5 file:px-3 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-zinc-950 file:text-white" />
        {file && <span className="text-xs text-zinc-600 truncate max-w-xs">{file.name}</span>}
        <label className="inline-flex items-center gap-1 text-xs text-zinc-700 ml-auto">
          <input type="checkbox" data-testid="input-material-visible" checked={form.visible} onChange={e => setForm({ ...form, visible: e.target.checked })} /> Visible to promoters
        </label>
      </div>
      {err && <div data-testid="material-create-error" className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{err}</div>}
      <button type="submit" disabled={saving} data-testid="btn-save-material"
        className="px-4 py-2 bg-zinc-950 text-white text-xs font-semibold rounded-full hover:bg-zinc-800 disabled:opacity-50 inline-flex items-center gap-1.5">
        {saving ? <Loader2 size={11} className="animate-spin" /> : <Plus size={11} />} Add material
      </button>
    </form>
  );
}

export default function AdminPromoterMaterials() {
  const [guide, setGuide] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [loadingGuide, setLoadingGuide] = useState(true);
  const [loadingMat, setLoadingMat] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const load = async () => {
    setLoadingMat(true);
    try {
      const [g, m] = await Promise.all([
        axiosAuth().get(`${API}/admin/promoter-guidelines`),
        axiosAuth().get(`${API}/admin/promoter-materials`),
      ]);
      setGuide(g.data); setMaterials(m.data || []);
    } finally { setLoadingGuide(false); setLoadingMat(false); }
  };
  useEffect(() => { load(); }, []);

  const saveGuide = async () => {
    setSaving(true);
    try {
      await axiosAuth().patch(`${API}/admin/promoter-guidelines`, {
        intro: guide.intro || "", dos: guide.dos || [], donts: guide.donts || [], terms: guide.terms || "",
      });
      setSaved(true); setTimeout(() => setSaved(false), 1500);
    } catch (e) { alert(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const toggleVisible = async (m) => {
    await axiosAuth().patch(`${API}/admin/promoter-materials/${m.material_id}`, { visible_to_promoters: !m.visible_to_promoters });
    load();
  };
  const remove = async (m) => {
    if (!window.confirm(`Delete "${m.title}"?`)) return;
    await axiosAuth().delete(`${API}/admin/promoter-materials/${m.material_id}`);
    load();
  };
  const download = async (m) => {
    try {
      const res = await axiosAuth().get(`${API}/admin/promoter-materials/${m.material_id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = m.file_filename || m.title; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { alert("Download failed"); }
  };

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="admin-promoter-materials-page">
      <header className="bg-white border-b border-zinc-200 px-6 py-4 flex items-center gap-3">
        <Link to="/admin/promoters" className="p-2 hover:bg-zinc-100 rounded-lg"><ArrowLeft size={16} /></Link>
        <BrandLogo variant="mark" size={32} />
        <h1 className="font-bold text-zinc-900">Promoter Guidelines & Materials</h1>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <div className="bg-emerald-50 border border-emerald-200 rounded-3xl p-5" data-testid="brand-kit-info-card">
          <h3 className="font-bold text-emerald-900 text-sm mb-1 flex items-center gap-2">
            <Package size={14} /> Brand kit (auto-included)
          </h3>
          <p className="text-xs text-emerald-800/80 leading-relaxed mb-3">
            Promoters already see and can download the official FastPrototype.ai logo set on their dashboard:
            <span className="font-mono"> fastprototype-logo.png</span>,
            <span className="font-mono"> fastprototype-wordmark.svg</span>,
            <span className="font-mono"> fastprototype-mark.svg</span>. You don't need to upload these — to update them, replace the files under <span className="font-mono text-[11px]">/app/frontend/public/brand/</span>.
          </p>
        </div>

        <div className="bg-white border border-zinc-200 rounded-3xl p-6" data-testid="guidelines-card">
          <h2 className="font-bold text-zinc-950 text-lg mb-3 flex items-center gap-2">
            <FileText size={16} /> Brand & Tone Guidelines
          </h2>
          {loadingGuide ? <Loader2 size={20} className="animate-spin text-zinc-400" /> : (
            <div className="space-y-5">
              <div>
                <label className="text-xs font-semibold text-zinc-600">Intro / positioning</label>
                <textarea rows={3} data-testid="input-guide-intro" value={guide?.intro || ""} onChange={e => setGuide({ ...guide, intro: e.target.value })}
                  placeholder="What FastPrototype.ai does, who it's for, the voice promoters should use."
                  className="mt-1 w-full px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900 resize-none" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <EditableList label="Do's — please do this" icon={ThumbsUp} color="emerald"
                  items={guide?.dos || []} setItems={(v) => setGuide({ ...guide, dos: v })} testIdPrefix="dos" />
                <EditableList label="Don'ts — please avoid" icon={ThumbsDown} color="red"
                  items={guide?.donts || []} setItems={(v) => setGuide({ ...guide, donts: v })} testIdPrefix="donts" />
              </div>
              <div>
                <label className="text-xs font-semibold text-zinc-600">Commission terms (plain-English summary)</label>
                <textarea rows={4} data-testid="input-guide-terms" value={guide?.terms || ""} onChange={e => setGuide({ ...guide, terms: e.target.value })}
                  placeholder="e.g. 15% commission on paid project revenue. Paid within 30 days of client final payment. Tracking window 90 days first-touch."
                  className="mt-1 w-full px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900 resize-none" />
              </div>
              <button onClick={saveGuide} disabled={saving} data-testid="btn-save-guidelines"
                className="px-5 py-2.5 bg-zinc-950 text-white text-sm font-semibold rounded-full hover:bg-zinc-800 disabled:opacity-50 inline-flex items-center gap-2">
                {saving ? <Loader2 size={14} className="animate-spin" /> : saved ? <><CheckCircle size={14} /> Saved</> : <><Save size={14} /> Save guidelines</>}
              </button>
            </div>
          )}
        </div>

        <div className="bg-white border border-zinc-200 rounded-3xl p-6" data-testid="materials-card">
          <h2 className="font-bold text-zinc-950 text-lg mb-1 flex items-center gap-2">
            <Package size={16} /> Materials library
          </h2>
          <p className="text-xs text-zinc-500 mb-4">Upload logos, screenshots, pitch decks, copy templates. Toggle visibility per item.</p>
          <MaterialForm onCreated={load} />
          <div className="mt-5 space-y-2" data-testid="materials-list">
            {loadingMat ? <Loader2 size={20} className="animate-spin text-zinc-400" />
              : materials.length === 0 ? <p className="text-sm text-zinc-500 py-3">No materials yet.</p>
              : materials.map(m => (
                <div key={m.material_id} data-testid={`material-${m.material_id}`}
                  className={`flex items-start justify-between gap-3 p-3 border rounded-xl ${m.visible_to_promoters ? "bg-white border-zinc-200" : "bg-zinc-50 border-zinc-200 opacity-75"}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="font-bold text-zinc-950 text-sm truncate">{m.title}</span>
                      <span className="text-[10px] font-bold uppercase tracking-wider bg-zinc-100 text-zinc-600 px-2 py-0.5 rounded-full">{m.category}</span>
                      {!m.visible_to_promoters && <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">hidden</span>}
                    </div>
                    {m.description && <div className="text-xs text-zinc-600">{m.description}</div>}
                    {m.body && <div className="text-xs text-zinc-500 mt-1 whitespace-pre-wrap">{m.body}</div>}
                    {m.file_filename && <div className="text-[11px] text-zinc-400 mt-1">📎 {m.file_filename} ({Math.round((m.file_size || 0) / 1024)} KB)</div>}
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    {m.file_id && <button onClick={() => download(m)} title="Download" className="p-1.5 text-zinc-700 hover:bg-zinc-100 rounded-lg" data-testid={`btn-download-material-${m.material_id}`}><Download size={13} /></button>}
                    <button onClick={() => toggleVisible(m)} title="Toggle visibility" className="p-1.5 text-zinc-700 hover:bg-zinc-100 rounded-lg" data-testid={`btn-toggle-material-${m.material_id}`}>
                      {m.visible_to_promoters ? <EyeOff size={13} /> : <Eye size={13} />}
                    </button>
                    <button onClick={() => remove(m)} title="Delete" className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg" data-testid={`btn-delete-material-${m.material_id}`}><Trash2 size={13} /></button>
                  </div>
                </div>
              ))
            }
          </div>
        </div>
      </div>
    </div>
  );
}
